import { FormEvent, useContext, useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import inputFields from './inputFields.json';
import SkillApi from '../../api/skillApi';
import Button from '../../components/atoms/button';
import DragAndDropButton from '../../components/atoms/button-drag-drop';
import Input from '../../components/atoms/input';
import TextArea from '../../components/atoms/text-area/TextArea';
import Page from '../../components/page-layout/Page';
import SkillService from '../../services/skillService';
import LearningObjectiveData from '../../types/learningObjective';
import SkillData from '../../types/skillData';
import AlertContext from '../../utils/AlertContext';
import { AuthContext } from '../../utils/AuthProvider';
import { getDefaultSkillImageUrl, randomHsl } from '../../utils/functions';
import { useFileUpload } from '../../utils/hooks';
import ModalContext from '../../utils/ModalContext';

import './CreateEditSkill.scss';

// Maximum image size in MBs
const MAX_IMAGE_SIZE = 5;
const ALLOWED_IMAGE_TYPES = ['jpg', 'png', 'jpeg', 'webp'];

function CreateEditSkill() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useContext(AuthContext)!;

  const [skill, setSkill] = useState<SkillData>();
  const [skillName, setSkillName] = useState<string>('');
  const [imageStoragePath, setImageStoragePath] = useState<string>('');
  const [skillDescription, setSkillDescription] = useState<string>('');
  const [los, setLos] = useState<LearningObjectiveData[]>([
    { id: uuidv4(), name: '', description: '', color: randomHsl() },
  ]);

  const [imageToUpload, setImageToUpload] = useState<File | 'DELETE_ONLY'>();
  const { previewUrl, updateStorage } = useFileUpload(
    imageToUpload,
    skill?.imageStoragePath || '',
    [imageStoragePath, setImageStoragePath],
    'images/skills'
  );

  const imageUrlRef = useRef<string>('');

  const { showConfirmModal } = useContext(ModalContext);
  const showAlert = useContext(AlertContext);

  useEffect(() => {
    async function fetchData() {
      if (!slug) return;
      const fetchedSkill = await SkillApi.getSkillBySlug(slug);
      setSkill(fetchedSkill);
      setSkillName(fetchedSkill.name);
      setSkillDescription(fetchedSkill.description);
      setLos(fetchedSkill.learningObjectives);
      setImageStoragePath(fetchedSkill.imageStoragePath);
      imageUrlRef.current = fetchedSkill.imageUrl;
    }

    fetchData();
  }, [slug]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const maybeImageUrl = await updateStorage();
    imageUrlRef.current = maybeImageUrl ? maybeImageUrl : getDefaultSkillImageUrl();
    if (skill) {
      await updateSkill(skill);
      showAlert('Changes saved!');
    } else {
      const addedSkill = await addSkill();
      navigate(`/skills/${addedSkill.slug}`);
    }
  }

  async function addSkill() {
    const newSkillId = await SkillService.addSkillAtEnd({
      authorUuid: user!.uid,
      name: skillName,
      imageStoragePath,
      imageUrl: imageUrlRef.current,
      description: skillDescription,
      learningObjectives: los,
    });

    return await SkillApi.getSkillById(newSkillId);
  }

  async function updateSkill(skill: SkillData) {
    const updatedSkill = {
      ...skill,
      name: skillName,
      description: skillDescription,
      learningObjectives: los,
      imageStoragePath,
      imageUrl: imageUrlRef.current,
    };

    await SkillApi.updateSkill(updatedSkill);
  }

  function handleDiscard() {
    showConfirmModal(() => navigate('/my-creations'));
  }

  return (
    <Page
      activeNavlink="my-creations"
      breadcrumbs={[
        ['/', 'Home'],
        ['/my-creations', 'Create'],
        ['#', !slug ? 'New skill' : 'Edit skill'],
      ]}
    >
      <div className="create-edit-skill">
        <ol className="breadcrumbs">
          <li className="breadcrumb">
            <Link to={'/my-creations'}>Create</Link>
          </li>
          <li className="breadcrumb">
            <span className="font-bold">{!slug ? 'New skill' : 'Edit skill'}</span>
          </li>
        </ol>
        <h1>Basic information</h1>
        <div className="main-container">
          <form onSubmit={handleSubmit}>
            <div className="mb-12">
              <h2 className="secondary-heading">Skill name</h2>
              <Input className="primary-input" state={[skillName, setSkillName]} {...inputFields.skillName} />
            </div>
            <div className="mb-12">
              <h2 className="secondary-heading">Upload cover image</h2>
              <DragAndDropButton
                setFile={setImageToUpload}
                maxFileSize={MAX_IMAGE_SIZE}
                allowedFileTypes={ALLOWED_IMAGE_TYPES}
              />
              {previewUrl && <img className="upload-image-preview" src={previewUrl} alt="User upload" />}
            </div>
            <h2 className="secondary-heading">Skill description</h2>
            <TextArea
              className="mb-12"
              state={[skillDescription, setSkillDescription]}
              {...inputFields.skillDescription}
            />
            <div className="form-action-buttons-container">
              <Button type="submit">Save</Button>
              <Button visualType="secondary" onClick={handleDiscard}>
                Discard
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Page>
  );
}

export default CreateEditSkill;
